/**
 * Background states and their changes (spec 039): the one writer of a
 * background's states (`writeBackgroundStates`, D-1, D-2), the rename and
 * removal of a state with its keys, the check every save runs
 * (`validateBackground`), and the pure resolution of the state playing at a
 * time with the change running then (`backgroundStateAt`, D-3 to D-5).
 * Store-free: the operations, the frame evaluator, the Service and the
 * editor (spec 040) all call these.
 */
import type { Background, BackgroundState, Keyframe, KeyframeTrack, Transition, TransitionDirection, TransitionType } from '../@types';
import { applyEasing, toKeyframeEasing } from './interpolate';

/** The reserved keyframe track of the background's state keys (D-3). */
export const BACKGROUND_TRACK_ID = 'background';
/** The property of a background state key (D-3). */
export const BACKGROUND_STATE_PROPERTY = 'state';
/** A state name is 1–40 characters after trimming. */
export const BACKGROUND_STATE_NAME_MAX = 40;
/** The name the current picture takes when a background gets its first states (D-2). */
export const FIRST_BACKGROUND_STATE = 'default';

const TRANSITION_TYPES: readonly TransitionType[] = ['none', 'fade', 'slide', 'zoom', 'wipe'];
const TRANSITION_DIRECTIONS: readonly TransitionDirection[] = ['left', 'right', 'up', 'down'];

type BackgroundLike = Partial<Pick<Background, 'states' | 'defaultState' | 'baseMediaId' | 'name' | 'layers'>> | null | undefined;

const picture = (id: unknown): number | undefined => (typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : undefined);
const fold = (name: string) => name.trim().toLowerCase();

/** The background's states, `[]` when it has none (absent, empty or not a list). */
export function backgroundStates(bg: BackgroundLike): BackgroundState[] {
  return Array.isArray(bg?.states) ? bg!.states!.filter((s) => s && typeof s.name === 'string') : [];
}

/** Whether the background has states (D-1). */
export const hasBackgroundStates = (bg: BackgroundLike): boolean => backgroundStates(bg).length > 0;

/** The default state's name: `defaultState` when it names a state, else the first state; undefined without states. */
export function backgroundDefaultState(bg: BackgroundLike): string | undefined {
  const states = backgroundStates(bg);
  if (states.length === 0) return undefined;
  const named = typeof bg?.defaultState === 'string' ? states.find((s) => s.name === bg.defaultState) : undefined;
  return (named ?? states[0]).name;
}

/** The state named `name` (exactly), or undefined. */
export const findBackgroundState = (bg: BackgroundLike, name: string | undefined): BackgroundState | undefined =>
  name === undefined ? undefined : backgroundStates(bg).find((s) => s.name === name);

/** The picture a state shows, when it has one (above 0). */
export const backgroundStatePicture = (bg: BackgroundLike, name: string | undefined): number | undefined =>
  picture(findBackgroundState(bg, name)?.mediaId);

/**
 * The placeholder label of a state without a picture (D-4 by 026's rules):
 * `<name> · <state>` when the background's name is set and not the default
 * `Background`, and it has no parallax layers; undefined otherwise.
 */
export function backgroundStatePlaceholder(bg: BackgroundLike, state: string): string | undefined {
  if ((bg?.layers ?? []).length > 0) return undefined;
  const name = (bg?.name ?? '').trim();
  if (!name || name === 'Background') return undefined;
  return `${name} · ${state}`;
}

export interface BackgroundStateError {
  /** Into the arguments: `states[1].name`, `defaultState`. */
  path: string;
  message: string;
}

/** A state name's problem, or undefined: trimmed, 1–40 characters. */
export function backgroundStateNameError(name: unknown): string | undefined {
  if (typeof name !== 'string' || name.trim().length < 1) return 'a state name must not be empty';
  if (name.trim().length > BACKGROUND_STATE_NAME_MAX) return `a state name is at most ${BACKGROUND_STATE_NAME_MAX} characters`;
  return undefined;
}

/**
 * The first problem of a list of states and a default, or undefined: at
 * least one state, names trimmed 1–40 characters and unique ignoring case,
 * pictures positive integers (0 or absent: none), `defaultState` (when
 * given) naming one of them ignoring case.
 */
export function checkBackgroundStates(states: readonly { name: unknown; mediaId?: unknown }[], defaultState?: unknown): BackgroundStateError | undefined {
  if (!Array.isArray(states) || states.length === 0) return { path: 'states', message: 'a background with states keeps at least one state' };
  const seen = new Set<string>();
  for (let i = 0; i < states.length; i++) {
    const s = states[i];
    const bad = backgroundStateNameError(s?.name);
    if (bad) return { path: `states[${i}].name`, message: bad };
    const key = fold(s.name as string);
    if (seen.has(key)) return { path: `states[${i}].name`, message: `two states are named "${(s.name as string).trim()}"` };
    seen.add(key);
    const m = s.mediaId;
    if (m !== undefined && m !== 0 && picture(m) === undefined) return { path: `states[${i}].mediaId`, message: 'mediaId must be a positive integer' };
  }
  if (defaultState !== undefined) {
    if (typeof defaultState !== 'string' || !seen.has(fold(defaultState))) {
      return { path: 'defaultState', message: `defaultState must name one of the states: ${states.map((s) => String(s.name).trim()).join(', ')}` };
    }
  }
  return undefined;
}

export type WriteBackgroundStatesResult =
  | { ok: true; background: Background }
  | { ok: false; error: BackgroundStateError };

/**
 * The one writer of a background's states (D-1, D-2). Names are trimmed;
 * a picture of 0 is no picture. When `bg` had no states and a picture,
 * that picture is kept as the state named `default` (taking it when the
 * list names `default` without a picture, prepended when the list does not
 * name it), and `default` is the default unless `defaultState` says
 * otherwise. `baseMediaId` is then set to the default state's picture, or
 * deleted when it has none. Returns a new `Background`; refuses (nothing
 * written) a list `checkBackgroundStates` refuses.
 */
export function writeBackgroundStates(
  bg: Background,
  states: readonly { name: string; mediaId?: number }[],
  defaultState?: string,
): WriteBackgroundStatesResult {
  const error = checkBackgroundStates(states, defaultState);
  if (error) return { ok: false, error };
  let next: BackgroundState[] = states.map((s) => {
    const mediaId = picture(s.mediaId);
    return mediaId !== undefined ? { name: s.name.trim(), mediaId } : { name: s.name.trim() };
  });
  let chosen = defaultState === undefined ? undefined : next.find((s) => fold(s.name) === fold(defaultState))!.name;
  const old = picture(bg.baseMediaId);
  if (!hasBackgroundStates(bg) && old !== undefined) {
    const at = next.findIndex((s) => fold(s.name) === FIRST_BACKGROUND_STATE);
    if (at === -1) next = [{ name: FIRST_BACKGROUND_STATE, mediaId: old }, ...next];
    else if (next[at].mediaId === undefined) next = next.map((s, i) => (i === at ? { ...s, mediaId: old } : s));
    if (chosen === undefined) chosen = next.find((s) => fold(s.name) === FIRST_BACKGROUND_STATE)!.name;
  }
  const def = chosen ?? next[0].name;
  const background: Background = { ...bg, states: next, defaultState: def };
  const base = picture(next.find((s) => s.name === def)?.mediaId);
  if (base !== undefined) background.baseMediaId = base;
  else delete background.baseMediaId;
  return { ok: true, background };
}

/** A document part the state writers change together: the background and the keyframe tracks. */
export interface BackgroundDoc {
  background: Background;
  keyframes?: KeyframeTrack[];
}

/** The background track's keys, `[]` when it has none. */
export const backgroundTrackKeys = (tracks: readonly KeyframeTrack[] | null | undefined): Keyframe[] =>
  (tracks ?? []).find((t) => t.trackId === BACKGROUND_TRACK_ID)?.keyframes ?? [];

/** `tracks` with the background track's keys replaced (the track dropped when empty). */
export function withBackgroundTrack(tracks: readonly KeyframeTrack[] | null | undefined, keys: Keyframe[]): KeyframeTrack[] {
  const list = [...(tracks ?? [])];
  const at = list.findIndex((t) => t.trackId === BACKGROUND_TRACK_ID);
  if (keys.length === 0) return at === -1 ? list : list.filter((_, i) => i !== at);
  const track = { trackId: BACKGROUND_TRACK_ID, keyframes: keys };
  if (at === -1) list.push(track);
  else list[at] = track;
  return list;
}

/**
 * `writeBackgroundStates` on `doc.background`, and the background track's
 * keys naming a state the new list lacks removed (a state dropped loses its
 * keys). Returns the new document part, or the refusal.
 */
export function setBackgroundStatesOf<D extends BackgroundDoc>(
  doc: D,
  states: readonly { name: string; mediaId?: number }[],
  defaultState?: string,
): { ok: true; doc: D; removedKeys: number } | { ok: false; error: BackgroundStateError } {
  const written = writeBackgroundStates(doc.background, states, defaultState);
  if (written.ok === false) return written;
  const names = new Set(backgroundStates(written.background).map((s) => s.name));
  const keys = backgroundTrackKeys(doc.keyframes);
  const kept = keys.filter((k) => k.property !== BACKGROUND_STATE_PROPERTY || (typeof k.value === 'string' && names.has(k.value)));
  const next = { ...doc, background: written.background } as D;
  if (kept.length !== keys.length) next.keyframes = withBackgroundTrack(doc.keyframes, kept);
  return { ok: true, doc: next, removedKeys: keys.length - kept.length };
}

/**
 * Renames a state and rewrites the background track's keys naming it (and
 * `defaultState` when it was the default). Refuses an unknown state or a
 * name `checkBackgroundStates` refuses.
 */
export function renameBackgroundState<D extends BackgroundDoc>(
  doc: D,
  from: string,
  to: string,
): { ok: true; doc: D } | { ok: false; error: BackgroundStateError } {
  const states = backgroundStates(doc.background);
  if (!states.some((s) => s.name === from)) {
    return { ok: false, error: { path: 'state', message: `no state "${from}"; states: ${states.map((s) => s.name).join(', ') || 'none'}` } };
  }
  const name = to.trim();
  const renamed = states.map((s) => (s.name === from ? { ...s, name } : s));
  const def = backgroundDefaultState(doc.background) === from ? name : backgroundDefaultState(doc.background);
  const written = writeBackgroundStates(doc.background, renamed, def);
  if (written.ok === false) return written;
  const keys = backgroundTrackKeys(doc.keyframes);
  const next = { ...doc, background: written.background } as D;
  if (keys.some((k) => k.value === from)) {
    next.keyframes = withBackgroundTrack(doc.keyframes, keys.map((k) => (k.property === BACKGROUND_STATE_PROPERTY && k.value === from ? { ...k, value: name } : k)));
  }
  return { ok: true, doc: next };
}

/** A transition's problem, or undefined: a known type, a duration of 0 or more ms, a known direction. */
function transitionError(t: unknown): string | undefined {
  if (!t || typeof t !== 'object' || Array.isArray(t)) return 'must be an object';
  const { type, duration, direction } = t as Transition;
  if (!TRANSITION_TYPES.includes(type)) return `type must be one of ${TRANSITION_TYPES.join(', ')}`;
  if (typeof duration !== 'number' || !Number.isFinite(duration) || duration < 0) return 'duration must be 0 or more milliseconds';
  if (direction !== undefined && !TRANSITION_DIRECTIONS.includes(direction)) return `direction must be one of ${TRANSITION_DIRECTIONS.join(', ')}`;
  return undefined;
}

/**
 * The check every saved scene's background passes (spec 039; the Service's
 * scene PATCH and `toPlayableScene`): `states`, when present, a list
 * `checkBackgroundStates` accepts (an empty list is a background with no
 * states), `defaultState` naming one of them, and a `transition` only on a
 * background track `state` key, of a known shape. Unknown fields stay
 * opaque. Undefined when it passes, else the first problem.
 */
export function validateBackground(bg: unknown, keyframes?: unknown): string | undefined {
  if (bg !== undefined && bg !== null) {
    if (typeof bg !== 'object' || Array.isArray(bg)) return 'background must be an object';
    const { states, defaultState } = bg as Background;
    if (states !== undefined) {
      if (!Array.isArray(states)) return 'background.states must be a list';
      if (states.length > 0) {
        const error = checkBackgroundStates(states, undefined);
        if (error) return `background.${error.path}: ${error.message}`;
        if (defaultState !== undefined && !states.some((s) => s.name === defaultState)) {
          return `background.defaultState must name one of the states: ${states.map((s) => s.name).join(', ')}`;
        }
      } else if (defaultState !== undefined) {
        return 'background.defaultState needs states';
      }
    } else if (defaultState !== undefined) {
      return 'background.defaultState needs states';
    }
  }
  if (Array.isArray(keyframes)) {
    for (const track of keyframes as KeyframeTrack[]) {
      for (const kf of Array.isArray(track?.keyframes) ? track.keyframes : []) {
        if (!kf || kf.transition === undefined) continue;
        if (track.trackId !== BACKGROUND_TRACK_ID || kf.property !== BACKGROUND_STATE_PROPERTY) {
          return `keyframe ${kf.id ?? ''} on track ${track.trackId}: a transition is only for a background state key`;
        }
        const bad = transitionError(kf.transition);
        if (bad) return `keyframe ${kf.id ?? ''}: transition ${bad}`;
      }
    }
  }
  return undefined;
}

/** A background state change at a moment (D-4): from one state to the playing one, eased progress 0..1. */
export interface BackgroundStateChange {
  from: string;
  type: TransitionType;
  direction?: TransitionDirection;
  progress: number;
}

/** One change of the background track (a key that changes the state). */
export interface BackgroundStateStep {
  time: number;
  from: string;
  to: string;
  transition?: Transition;
}

/**
 * The background's changes in time order (D-3): its `state` keys naming a
 * state, stepped from the default; a key repeating the state playing then,
 * or naming no state, changes nothing. `[]` without states.
 */
export function backgroundStateSteps(bg: BackgroundLike, tracks: readonly KeyframeTrack[] | null | undefined): BackgroundStateStep[] {
  const def = backgroundDefaultState(bg);
  if (def === undefined) return [];
  const names = new Set(backgroundStates(bg).map((s) => s.name));
  const keys = backgroundTrackKeys(tracks)
    .filter((k) => k && k.property === BACKGROUND_STATE_PROPERTY && typeof k.value === 'string' && names.has(k.value) && Number.isFinite(k.time))
    .map((k, i) => ({ k, i }))
    .sort((a, b) => a.k.time - b.k.time || a.i - b.i)
    .map(({ k }) => k);
  const steps: BackgroundStateStep[] = [];
  let current = def;
  for (const k of keys) {
    const to = k.value as string;
    if (to === current) continue;
    steps.push({ time: k.time, from: current, to, ...(k.transition ? { transition: k.transition } : {}) });
    current = to;
  }
  return steps;
}

/** Whether a transition draws (a type other than `none` and a duration above 0). */
const draws = (t: Transition | undefined): t is Transition =>
  !!t && t.type !== 'none' && TRANSITION_TYPES.includes(t.type) && typeof t.duration === 'number' && t.duration > 0;

/**
 * The state playing at `tMs` and the change running then (D-3 to D-5):
 * the latest change at or before `tMs` sets the state (the default before
 * any); while its transition runs (`[time, time + duration)`), `change`
 * goes from the state playing when its key was reached (a change still
 * running then counts as fully shown, D-5) with progress eased by the
 * transition's `ease`. A background with no states gives `{}`.
 */
export function backgroundStateAt(
  bg: BackgroundLike,
  tracks: readonly KeyframeTrack[] | null | undefined,
  tMs: number,
): { state?: string; change?: BackgroundStateChange } {
  const def = backgroundDefaultState(bg);
  if (def === undefined) return {};
  const steps = backgroundStateSteps(bg, tracks);
  let last: BackgroundStateStep | undefined;
  for (const step of steps) {
    if (step.time <= tMs) last = step;
    else break;
  }
  if (!last) return { state: def };
  const t = last.transition;
  if (!draws(t) || tMs >= last.time + t.duration) return { state: last.to };
  const linear = Math.min(1, Math.max(0, (tMs - last.time) / t.duration));
  const change: BackgroundStateChange = { from: last.from, type: t.type, progress: applyEasing(linear, toKeyframeEasing(t.ease)) };
  if (t.direction) change.direction = t.direction;
  return { state: last.to, change };
}

/** The latest moment a background change is still drawing (key time plus its transition), 0 without one. */
export function backgroundChangesEnd(tracks: readonly KeyframeTrack[] | null | undefined): number {
  let end = 0;
  for (const k of backgroundTrackKeys(tracks)) {
    if (k && Number.isFinite(k.time) && draws(k.transition)) end = Math.max(end, k.time + k.transition.duration);
  }
  return end;
}
